"use client";

import type FansUnitedSDKModel from "fansunited-sdk-esm/Core/Global/Models/FansUnitedSDKModel";

type Sdk = FansUnitedSDKModel | null | undefined;

/**
 * One consent the user is accepting now. `content` is recorded by the backend
 * as a textual snapshot of what the user agreed to (extracted from the rich
 * text body via {@link richTextToPlainText}).
 */
export interface ConsentSubmission {
    consentId: string;
    content?: string | null;
}

/**
 * Fetch the set of consentIds the current user has accepted.
 * Returns an empty Set if the SDK is unavailable or the call fails
 * (treated as "no consents accepted yet" — UI prompts appropriately).
 */
export async function getAcceptedConsentIds(sdk: Sdk): Promise<Set<string>> {
    if (!sdk) return new Set();
    try {
        const consents = await sdk.profile.getOwnConsents();
        return new Set(consents.map((c) => c.consentId));
    } catch (err) {
        console.warn("[profile-consent] failed to load own consents:", err);
        return new Set();
    }
}

/**
 * Submit consent acceptances to the FansUnited backend.
 * Throws on failure — callers decide whether to surface the error or swallow it.
 */
export async function submitConsents(sdk: Sdk, submissions: ConsentSubmission[]): Promise<void> {
    if (!sdk) throw new Error("SDK not available");
    if (submissions.length === 0) return;
    await sdk.profile.collectConsents(
        submissions.map((s) => ({
            consentId: s.consentId,
            content: s.content ?? null,
        }))
    );
}

/** Withdraw a previously-accepted consent. Throws on failure. */
export async function withdrawConsent(sdk: Sdk, consentId: string): Promise<void> {
    if (!sdk) throw new Error("SDK not available");
    await sdk.profile.deleteConsent(consentId);
}

/**
 * Flatten a Lexical rich-text node tree into a plain-text string. Used to populate
 * the `content` field on consent submissions so the backend records what text the
 * user actually saw and agreed to.
 *
 * Walks the tree depth-first, joining text leaves with spaces and collapsing
 * whitespace. Links and other inline elements contribute their inner text.
 */
export function richTextToPlainText(content: unknown): string {
    if (!content) return "";

    const visit = (node: unknown): string => {
        if (!node || typeof node !== "object") return "";
        const n = node as { text?: unknown; children?: unknown[]; root?: { children?: unknown[] } };
        if (typeof n.text === "string") return n.text;
        const children = (n.root?.children ?? n.children ?? []) as unknown[];
        return children.map(visit).join(" ");
    };

    return visit(content).replace(/\s+/g, " ").trim();
}
